/**
 * La tabla de cuentas de la zona 2, en sus dos variantes.
 *
 *  - `consumo`: el desglose por cliente del período. Créditos, costo de servir,
 *    costo por traza y por agente, y la cadena de la cuenta en chico.
 *  - `expansion`: las cuentas que crecieron. Mismas columnas, otro orden de
 *    lectura y otra conversación: acá el número que importa es cuántos agentes
 *    tiene activos contra cuántos contrató.
 *
 * Es un componente y no dos porque las dos listas son `UsageClientRow[]` y
 * comparten cada regla de celda: `null` es "Sin dato" y no cero, cada variación
 * publica su base, y el costo por agente se reparte entre los **activos** —
 * repartirlo entre los contratados abarataría justamente a la cuenta que peor
 * está.
 *
 * **Ni una columna de facturación.** Lo que paga el cliente y el margen viven en
 * `EconomiaBlock`, que solo se monta para admin. Esta tabla se puede servir a
 * cualquiera de los tres roles tal cual está, y esa es la razón de que el corte
 * del módulo sea por bloque y no por celda condicional repartida por la pantalla.
 */
import type { Links, UsageClientRow } from '@ops/contracts'
import { CLIENT_STATE_LABELS, formatMetricValue } from '@ops/contracts'

import { formatPeriodoCorto, plural } from '../../lib/format'
import { rutaCliente } from '../../lib/rutas'
import {
  Button,
  DataTable,
  DeltaTag,
  MissingCell,
  Panel,
  ProvenanceBadge,
  Sparkline,
  StatusPill,
  type Column,
} from '../../ui'
import { Vacio } from '../comunes'
import { baseDeVariacion, formatUsdUnitario, severidadDeCaida } from './derivar'
import { Link } from 'react-router-dom'

export type VarianteCuentas = 'consumo' | 'expansion'

export interface TablaCuentasProps {
  variante: VarianteCuentas
  filas: readonly UsageClientRow[]
  links: Links
  titulo: string
  subtitulo?: React.ReactNode
  /** Qué decir cuando la lista está vacía, y por qué puede estarlo. */
  vacio: string
  vacioDetalle?: string
  footer?: React.ReactNode
  umbralCaidaPct: number
  onCrearSeguimiento?: (fila: UsageClientRow) => void
  now: Date
  id?: string
}

/** La serie del sparkline, dicha en palabras: el gráfico no se lee con un lector. */
function etiquetaSerie(fila: UsageClientRow): string {
  const puntos = fila.serie
    .map(
      (p) =>
        `${formatPeriodoCorto(p.periodo)} ${
          p.creditos === null
            ? 'sin medición'
            : formatMetricValue(p.creditos, 'count')
        }`,
    )
    .join(', ')
  return `Créditos de ${fila.clientNombre} mes a mes: ${puntos}`
}

export function TablaCuentas({
  variante,
  filas,
  titulo,
  subtitulo,
  vacio,
  vacioDetalle,
  footer,
  umbralCaidaPct,
  onCrearSeguimiento,
  now,
  id,
}: TablaCuentasProps) {
  const columnas: ReadonlyArray<Column<UsageClientRow>> = [
    {
      key: 'cuenta',
      header: 'Cuenta',
      rowHeader: true,
      width: '200px',
      sortValue: (f) => f.clientNombre,
      filterValue: (f) => `${f.clientNombre} ${CLIENT_STATE_LABELS[f.estado]}`,
      render: (f) => (
        <span className="flex min-w-0 flex-col gap-0.5">
          <Link
            to={rutaCliente(f.clientId)}
            className="truncate font-semibold text-accent-text underline decoration-from-font underline-offset-2 hover:text-text"
          >
            {f.clientNombre}
          </Link>
          <StatusPill severidad="unknown" size="sm" icono={false}>
            {CLIENT_STATE_LABELS[f.estado]}
          </StatusPill>
        </span>
      ),
    },
    {
      key: 'creditos',
      header: 'Créditos',
      align: 'right',
      width: '130px',
      sortValue: (f) => f.creditosConsumidos,
      render: (f) =>
        f.creditosConsumidos === null ? (
          <MissingCell que="medición del período" />
        ) : (
          <span className="tabular">
            {formatMetricValue(f.creditosConsumidos, 'count')}
          </span>
        ),
    },
    {
      key: 'variacion',
      header: 'Contra el mes anterior',
      width: '230px',
      sortValue: (f) => f.variacionCreditos?.pct ?? null,
      render: (f) =>
        f.variacionCreditos === null ? (
          <MissingCell que="mes anterior comparable" />
        ) : (
          <DeltaTag
            deltaPct={f.variacionCreditos.pct}
            base={baseDeVariacion(f.variacionCreditos)}
            unidad="count"
            direccion="higher_better"
            size="sm"
          />
        ),
    },
    {
      key: 'agentes',
      header: 'Empleados digitales',
      width: '230px',
      sortValue: (f) => f.agentes.activos,
      render: (f) => (
        <span className="flex flex-col gap-0.5">
          <span className="tabular">
            <b className="font-semibold">
              {formatMetricValue(f.agentes.activos, 'count')}
            </b>{' '}
            {plural(f.agentes.activos, 'activo', 'activos')} de{' '}
            {formatMetricValue(f.agentes.instalados, 'count')}{' '}
            {plural(f.agentes.instalados, 'instalado', 'instalados')}
          </span>
          <span className="text-micro text-muted-strong">
            {formatMetricValue(f.agentes.contratados, 'count')} contratados
            {f.agentes.sinMedicion > 0 ? (
              <>
                {' · '}
                {formatMetricValue(f.agentes.sinMedicion, 'count')} sin medición
              </>
            ) : null}
          </span>
        </span>
      ),
    },
    {
      key: 'costo',
      header: 'Costo de servir',
      align: 'right',
      width: '140px',
      sortValue: (f) => f.costoUsd,
      render: (f) =>
        f.costoUsd === null ? (
          <MissingCell que="medición del período" />
        ) : (
          <span className="tabular">
            {formatMetricValue(f.costoUsd, 'usd')}
          </span>
        ),
    },
    {
      key: 'unitario',
      header: 'Por agente · por traza',
      align: 'right',
      width: '200px',
      sortValue: (f) => f.costoPorAgente,
      render: (f) => (
        /* Los dos derivados con su divisor dicho en el pie del panel: por agente
           **activo**, no por contratado. */
        <span className="tabular text-micro text-muted-strong">
          {f.costoPorAgente === null
            ? 'sin agente activo'
            : formatUsdUnitario(f.costoPorAgente)}{' '}
          ·{' '}
          {f.costoPorTraza === null
            ? 'sin trazas'
            : formatUsdUnitario(f.costoPorTraza)}
        </span>
      ),
    },
    {
      key: 'serie',
      header: 'Últimos 6 meses',
      width: '120px',
      render: (f) => (
        <Sparkline
          valores={f.serie.map((p) => p.creditos)}
          health={severidadDeCaida(f.variacionCreditos?.pct, umbralCaidaPct)}
          label={etiquetaSerie(f)}
        />
      ),
    },
    {
      key: 'fuente',
      header: 'Fuente',
      width: '170px',
      render: (f) => (
        <ProvenanceBadge
          provenance={f.provenance}
          now={now}
          variant="inline"
          hrefAdmin="/admin"
        />
      ),
    },
    ...(onCrearSeguimiento === undefined
      ? []
      : [
          {
            key: 'accion',
            header: 'Seguimiento',
            width: '150px',
            render: (f: UsageClientRow) => (
              <Button
                variante="chico"
                icono="mas"
                onClick={() => onCrearSeguimiento(f)}
              >
                {variante === 'expansion' ? 'Abrir expansión' : 'Asignar seguimiento'}
                <span className="sr-only"> de {f.clientNombre}</span>
              </Button>
            ),
          } satisfies Column<UsageClientRow>,
        ]),
  ]

  if (filas.length === 0) {
    return (
      <div id={id} className="min-w-0">
        <Panel titulo={titulo} padding="sm">
          <Vacio
            icono={null}
            mensaje={vacio}
            {...(vacioDetalle === undefined ? {} : { detalle: vacioDetalle })}
          />
        </Panel>
      </div>
    )
  }

  return (
    <div id={id} className="min-w-0">
      <Panel
        titulo={titulo}
        {...(subtitulo === undefined ? {} : { subtitulo })}
        padding="none"
        footer={
          footer ?? (
            <>
              El costo por agente se reparte entre los <b>activos</b>, no entre
              los contratados: dividirlo por los contratados abarataría a la
              cuenta que menos usa lo que compró.
            </>
          )
        }
      >
        <DataTable
          columns={columnas}
          rows={filas}
          rowKey={(f) => f.clientId}
          rowSeverity={(f) =>
            variante === 'expansion'
              ? 'ok'
              : severidadDeCaida(f.variacionCreditos?.pct, umbralCaidaPct)
          }
          density="compacta"
          filtroPlaceholder="cuenta o estado"
          emptyMessage={vacio}
          caption={
            variante === 'expansion' ? (
              <>
                Cuentas cuyo consumo creció contra el mes anterior. Consumo y
                costo salen de Langfuse; los empleados digitales, de la
                plataforma.
              </>
            ) : (
              <>
                Consumo del período por cuenta: créditos, costo de servir y sus
                unitarios. Medido por Langfuse y atribuido con la plataforma.
              </>
            )
          }
        />
      </Panel>
    </div>
  )
}
